import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Line } from 'react-native-svg';

import {
	VipSubscriptionBenefit1,
	VipSubscriptionBenefit2,
	VipSubscriptionBenefit3,
	VipSubscriptionBenefit4,
	VipSubscriptionBenefit5,
	VipSubscriptionHeroLogo,
} from '../../../assets';
import barIcon from '../../../assets/main/vipButton/bar.png';
import { testProps } from '../../../config';
import { playSound } from '../../../utils/sound';
import { RootStackParamList } from '../../navigator';
import GradientPlate, { GradientStops } from '../common/GradientPlate';
import { IndicatorScrollView } from '../common/ScrollIndicator';
import { Pressable } from '../common/Touchable';
import {
	FittedGoldLine,
	FONT,
	iconGlow,
	REWARD_CARD_BORDER,
	REWARD_CARD_FILL,
	SUBSCRIBE_FILL,
} from '../Profile/BuyVip/vipClubShared';
import RedesignModal from './RedesignModal';
import { useRedesignScale } from './useRedesignScale';

/**
 * The "Become VIP" upsell. There is no Figma frame for it; it is drawn in the redesign popups'
 * chrome (see RedesignConfirmModal) with the VIP Club page's artwork: its hero logo, a gold
 * gradient title, the benefit icons that go with the first five benefits, and its green
 * subscribe button. The list scrolls when the panel is shorter than the design (landscape phones).
 * Numbers are design units, scaled once.
 */
const PANEL = { height: 560, radius: 17, rim: 2 };
const LAYOUT = {
	paddingTop: 22,
	side: 16,
	logoWidth: 196,
	logoHeight: 97,
	titleTop: 6,
	titleHeight: 28,
	listTop: 10,
	rowHeight: 56,
	rowGap: 6,
	icon: 38,
	buttonTop: 14,
	buttonWidth: 220,
	buttonHeight: 48,
	bottom: 20,
};
const CLOSE = { size: 37, right: -7, top: 1 };
const BENEFIT_ICONS = [
	VipSubscriptionBenefit1,
	VipSubscriptionBenefit2,
	VipSubscriptionBenefit3,
	VipSubscriptionBenefit4,
	VipSubscriptionBenefit5,
];

const colors = {
	text: '#FFF8F4',
	highlight: '#FFB321',
	panelRim: '#FFC261',
	panelTint: 'rgba(8,0,0,0.5)',
	scrim: 'rgba(0,0,0,0.68)',
} as const;

const CLOSE_FILL: GradientStops = [
	['0', '#B22306'],
	['0.5', '#6E0900'],
	['1', '#A00C00'],
];
const CLOSE_RIM: GradientStops = [
	['0', '#FFEA96'],
	['0.5', '#FFB844'],
	['1', '#FF7F23'],
];
const BUTTON_RIM: GradientStops = [
	['0', '#B6FF8E'],
	['0.5', '#5CDB3A'],
	['1', '#2B9A20'],
];

/** Keeps an icon above its absolutely positioned GradientPlate on web (see FriendsFilterModal). */
const IconBox = ({ children }: { children: React.ReactNode }) => (
	<View pointerEvents='none'>{children}</View>
);

interface BecomeVipModalProps {
	onClose: () => void;
}

type Nav = NativeStackNavigationProp<RootStackParamList>;

const BecomeVipModal = ({ onClose }: BecomeVipModalProps) => {
	const { t } = useTranslation();
	const { navigate } = useNavigation<Nav>();
	const scale = useRedesignScale({ fitHeight: PANEL.height, minFitScale: 0.8 });
	const s = (n: number) => n * scale;
	const styles = useMemo(() => createStyles(scale), [scale]);

	// The modal is mounted only while shown (rendered with `visible`), so mount == open.
	useEffect(() => {
		playSound('Notification'); // v1 modal.become.vip.component
	}, []);

	const handleBecomeVip = () => {
		onClose();
		setTimeout(() => navigate('BuyPackages', { activeTab: 'vip' }), 0);
	};

	// The benefit copy comes from the server as HTML: `<span>` marks the highlighted words and the
	// last one ends in an `<img>` of the hand-power meter.
	const parseTextWithSpan = (text: string) => {
		const spanRegex = /<span>(.*?)<\/span>/g;
		const parts: (string | React.ReactNode)[] = [];
		let lastIndex = 0;
		let match;

		while ((match = spanRegex.exec(text)) !== null) {
			if (match.index > lastIndex) {
				parts.push(text.substring(lastIndex, match.index));
			}
			parts.push(
				<Text key={match.index} style={styles.highlight}>
					{match[1]}
				</Text>,
			);
			lastIndex = spanRegex.lastIndex;
		}

		if (lastIndex < text.length) {
			parts.push(text.substring(lastIndex));
		}

		return parts.length > 0 ? parts : text;
	};

	const renderBenefit = (htmlString: string) => {
		const [text, ...rest] = htmlString.split(/<img[^>]*>/);
		return (
			<View style={styles.benefitTextBox}>
				<Text style={styles.benefitText}>
					{parseTextWithSpan(text.trim())}
					{rest.length > 0 ? ' ' : null}
				</Text>
				{rest.length > 0 ? (
					<Image source={barIcon} style={styles.barIcon} resizeMode='contain' />
				) : null}
			</View>
		);
	};

	const closeButton = (
		<Pressable
			accessibilityRole='button'
			accessibilityLabel={t('T_SITE_ACHIEVEMENTS_CLOSE', { defaultValue: 'Close' })}
			onPress={onClose}
			hitSlop={6}
			style={styles.close}
		>
			<GradientPlate
				width={s(CLOSE.size)}
				height={s(CLOSE.size)}
				radius={s(CLOSE.size / 2)}
				stops={CLOSE_FILL}
				strokeStops={CLOSE_RIM}
				strokeWidth={Math.max(1, s(2))}
			/>
			<IconBox>
				<Svg width={s(15)} height={s(15)} viewBox='0 0 15 15'>
					<Line
						x1={2}
						y1={2}
						x2={13}
						y2={13}
						stroke='#FFEAA4'
						strokeWidth={2.8}
						strokeLinecap='round'
					/>
					<Line
						x1={13}
						y1={2}
						x2={2}
						y2={13}
						stroke='#FFEAA4'
						strokeWidth={2.8}
						strokeLinecap='round'
					/>
				</Svg>
			</IconBox>
		</Pressable>
	);

	const rowWidth = s(358 - LAYOUT.side * 2);

	return (
		<RedesignModal
			scale={scale}
			onClose={onClose}
			panel={{
				height: PANEL.height,
				radius: PANEL.radius,
				borderColor: colors.panelRim,
				borderWidth: PANEL.rim,
				tint: colors.panelTint,
				scrimColor: colors.scrim,
				overScreen: true,
			}}
			panelOverlay={closeButton}
		>
			<View style={styles.body}>
				<Image source={VipSubscriptionHeroLogo} style={styles.logo} resizeMode='contain' />

				<View style={styles.title}>
					<FittedGoldLine
						text={t('T_SITE_SHOP_VIP_WHAT')}
						maxWidth={rowWidth}
						height={s(LAYOUT.titleHeight)}
						fontSize={s(23)}
						align='center'
						shadow
					/>
				</View>

				<IndicatorScrollView
					style={styles.list}
					contentContainerStyle={styles.listContent}
					reserveGutter={false}
					nestedScrollEnabled
				>
					{BENEFIT_ICONS.map((icon, i) => (
						<View key={i} style={styles.row}>
							<GradientPlate
								width={rowWidth}
								height={s(LAYOUT.rowHeight)}
								radius={s(10)}
								stops={REWARD_CARD_FILL}
								stroke={REWARD_CARD_BORDER}
								strokeWidth={Math.max(1, s(1))}
							/>
							<Image source={icon} style={[styles.icon, iconGlow(s(5))]} resizeMode='contain' />
							{renderBenefit(t(`T_SITE_SHOP_VIP_WHAT_${i + 1}`))}
						</View>
					))}
				</IndicatorScrollView>

				<Pressable
					accessibilityRole='button'
					onPress={handleBecomeVip}
					style={styles.button}
					{...testProps('cta-become-vip')}
				>
					<GradientPlate
						width={s(LAYOUT.buttonWidth)}
						height={s(LAYOUT.buttonHeight)}
						radius={s(12)}
						stops={SUBSCRIBE_FILL}
						strokeStops={BUTTON_RIM}
						strokeWidth={Math.max(1, s(1.5))}
					/>
					<Text numberOfLines={1} style={styles.buttonLabel}>
						{t('T_SITE_BUY_VIP').toUpperCase()}
					</Text>
				</Pressable>
			</View>
		</RedesignModal>
	);
};

export default BecomeVipModal;

const createStyles = (scale: number) => {
	const s = (n: number) => n * scale;
	return StyleSheet.create({
		body: {
			flex: 1,
			paddingTop: s(LAYOUT.paddingTop),
			paddingBottom: s(LAYOUT.bottom),
			alignItems: 'center',
		},
		logo: {
			width: s(LAYOUT.logoWidth),
			height: s(LAYOUT.logoHeight),
		},
		title: {
			marginTop: s(LAYOUT.titleTop),
		},
		list: {
			flex: 1,
			width: '100%',
			marginTop: s(LAYOUT.listTop),
		},
		listContent: {
			alignItems: 'center',
			gap: s(LAYOUT.rowGap),
		},
		row: {
			width: s(358 - LAYOUT.side * 2),
			height: s(LAYOUT.rowHeight),
			flexDirection: 'row',
			alignItems: 'center',
			paddingHorizontal: s(10),
			gap: s(10),
		},
		icon: {
			width: s(LAYOUT.icon),
			height: s(LAYOUT.icon),
		},
		benefitTextBox: {
			flex: 1,
			flexDirection: 'row',
			flexWrap: 'wrap',
			alignItems: 'center',
		},
		benefitText: {
			fontFamily: FONT,
			fontSize: s(14),
			lineHeight: s(18),
			fontWeight: '700',
			color: colors.text,
		},
		highlight: {
			color: colors.highlight,
			fontWeight: '900',
		},
		barIcon: {
			width: s(54),
			height: s(10),
		},
		button: {
			width: s(LAYOUT.buttonWidth),
			height: s(LAYOUT.buttonHeight),
			marginTop: s(LAYOUT.buttonTop),
			borderRadius: s(12),
			alignItems: 'center',
			justifyContent: 'center',
			shadowColor: '#2BFF5A',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.45,
			shadowRadius: s(8),
		},
		buttonLabel: {
			fontFamily: FONT,
			fontSize: s(18),
			fontWeight: '900',
			color: '#FFFFFF',
			textShadowColor: 'rgba(0,60,0,0.55)',
			textShadowOffset: { width: 0, height: s(1) },
			textShadowRadius: s(1.5),
		},
		close: {
			position: 'absolute',
			right: s(CLOSE.right),
			top: s(CLOSE.top),
			width: s(CLOSE.size),
			height: s(CLOSE.size),
			borderRadius: s(CLOSE.size / 2),
			alignItems: 'center',
			justifyContent: 'center',
			shadowColor: '#FF6A00',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.6,
			shadowRadius: s(6),
		},
	});
};
